import { forwardRef, useId } from 'react';
import type { ReactNode } from 'react';

import { cn } from 'components/utils';

import {
  contentBlockContentRecipe,
  contentBlockDescriptionRecipe,
  contentBlockFooterRecipe,
  contentBlockHeaderRecipe,
  contentBlockRootRecipe,
  contentBlockTitleRecipe,
} from './ContentBlock.recipe';

import type { ContentBlockProps } from './types';

/**
 * Whether a node would render nothing at all, so its wrapper can be left out entirely.
 *
 * The problem this solves is that `footer && <div/>` is not enough. A caller writes
 * `footer={isPublished && <PublishRow/>}`, the condition is false, and React receives `false` —
 * which renders nothing, but a `footer !== undefined` check has already rendered the wrapper
 * around it. The result is a 12px gap under the body with nothing in it, for a reason invisible in
 * both the markup and the props.
 *
 * So the four falsy shapes a conditional expression actually produces are treated as absent:
 * `null`, `undefined`, either boolean, and the empty string. An array is recursed, because
 * `{items.map(…)}` over an empty list and `[cond && <A/>, cond && <B/>]` are the same situation one
 * level down.
 *
 * ── `0` is NOT absent, and that is the whole reason this is not `!node` ──────────────────────
 *
 * `0` is a legitimate thing to render — "0 responses", a count, a duration — and it is falsy. A
 * `!node` guard would silently swallow it, which is the classic React rendering bug and the one
 * this helper exists to avoid reintroducing. `''` is excluded and `0` is not, deliberately: an
 * empty string is the absence of text, a zero is a value.
 *
 * ── What it CANNOT see, which is documented rather than worked around ────────────────────────
 *
 * A React ELEMENT is opaque. `<></>`, `<Foo/>` where `Foo` returns `null`, and a component that
 * renders only whitespace are all indistinguishable from an element with content, because
 * answering the question would mean rendering it. Those stay the caller's responsibility, and the
 * props docs say so. There is no way around it short of a ref-measuring effect, which would move
 * a layout decision into a second paint.
 */
const isEmptyNode = (node: ReactNode): boolean => {
  if (node === null || node === undefined || typeof node === 'boolean' || node === '') return true;

  return Array.isArray(node) && node.every((child) => isEmptyNode(child as ReactNode));
};

/**
 * Design System 2.0 static titled region. Use Accordion for expandable content and a plain
 * container for untitled content; ContentPanel is deprecated.
 *
 * Valid cells: filled/stacked (default), filled/inline, and none/inline. A blank title and
 * none/stacked throw. The heading names the region automatically. See `implementation-notes.md`
 * for the Figma mapping, accessibility reasoning and runtime invariants.
 */
export const ContentBlock = forwardRef<HTMLElement, ContentBlockProps>(
  ({ children, className, description, footer, headingLevel = 2, layout, surface = 'filled', title, ...rest }, ref) => {
    if (title.trim() === '') {
      throw new Error('ContentBlock requires a non-empty title.');
    }

    if (surface === 'none' && layout === 'stacked') {
      throw new Error('ContentBlock does not support layout="stacked" with surface="none".');
    }

    // Read off the surface rather than defaulted in the signature — see the note above on why a
    // single default would make the shortest no-surface call throw.
    const resolvedLayout = layout ?? (surface === 'none' ? 'inline' : 'stacked');

    const headingId = useId();
    // A template rather than a lookup table: the level is constrained to 1-6 by the props type and
    // again by the runtime shape of the token layer, so there is no invalid tag this can produce.
    const Heading = `h${headingLevel}` as const;

    const hasDescription = !isEmptyNode(description);
    const hasContent = !isEmptyNode(children);
    const hasFooter = !isEmptyNode(footer);

    return (
      <section
        ref={ref}
        className={cn(contentBlockRootRecipe({ layout: resolvedLayout, surface }), className)}
        {...rest}
        aria-labelledby={headingId}
        data-layout={resolvedLayout}
        data-part="root"
        data-surface={surface}
      >
        <div className={contentBlockHeaderRecipe()} data-part="header">
          <Heading id={headingId} className={contentBlockTitleRecipe()} data-part="title">
            {title}
          </Heading>

          {hasDescription && (
            <p className={contentBlockDescriptionRecipe()} data-part="description">
              {description}
            </p>
          )}
        </div>

        {hasContent && (
          <div className={contentBlockContentRecipe()} data-part="content">
            {children}
          </div>
        )}

        {hasFooter && (
          <div className={contentBlockFooterRecipe()} data-part="footer">
            {footer}
          </div>
        )}
      </section>
    );
  }
);
